if (!customElements.get('bdm-model-checker')) {
  class BdmModelChecker extends HTMLElement {
    constructor() {
      super();

      this.checkButton = this.querySelector('.js-bdm-check');
      this.statusEl = this.querySelector('.s-bdm-model-checker__status');
      this.resultEl = this.querySelector('.s-bdm-model-checker__result');
      this.modelEl = this.querySelector('.js-bdm-model');
      this.deviceEl = this.querySelector('.js-bdm-device');

      this.vendorId = 0x054C;
      this.productId = 0x0CE6;
      this.reportId = 0x20;
      this.bdmMap = {
        0x03: 'BDM-010',
        0x04: 'BDM-020',
        0x05: 'BDM-030',
        0x06: 'BDM-040',
        0x07: 'BDM-050',
        0x08: 'BDM-050',
        0x11: 'BDM-060M',
        0x13: 'BDM-060X'
      };

      if (this.checkButton) {
        this.checkButton.addEventListener('click', this.handleCheckClick.bind(this));
      }

      this.setStatus(this.dataset.msgReady || 'Ready');
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message;
    }

    setLoadingState(isLoading) {
      if (!this.checkButton) return;
      this.checkButton.disabled = isLoading;
      this.checkButton.classList.toggle('is-loading', isLoading);
    }

    setResult(model, deviceLabel) {
      if (!this.resultEl || !this.modelEl || !this.deviceEl) return;
      this.modelEl.textContent = model;
      this.deviceEl.textContent = deviceLabel;
      this.resultEl.hidden = false;
    }

    hideResult() {
      if (this.resultEl) this.resultEl.hidden = true;
    }

    getDeviceLabel(device) {
      const productName = device.productName || 'DualSense';
      const productId = `0x${Number(device.productId || 0).toString(16).toUpperCase().padStart(4, '0')}`;
      return `${productName} (${productId})`;
    }

    getMappedModel(bdmByte) {
      return this.bdmMap[bdmByte] || (this.dataset.msgUnknownModel || 'Unknown');
    }

    async requestDualSenseDevice() {
      const devices = await navigator.hid.requestDevice({
        filters: [{ vendorId: this.vendorId, productId: this.productId }]
      });

      if (!devices || devices.length === 0) {
        throw new DOMException('No device selected', 'NotFoundError');
      }

      return devices[0];
    }

    parseBdmModel(reportData) {
      if (!reportData || typeof reportData.byteLength !== 'number' || reportData.byteLength < 28) {
        throw new Error('Invalid feature report payload');
      }

      const hwInfo = reportData.getUint32(24, true);
      const bdmByte = (hwInfo >> 8) & 0xff;
      return this.getMappedModel(bdmByte);
    }

    mapErrorToMessage(error) {
      if (!('hid' in navigator)) {
        return this.dataset.msgUnsupported;
      }

      if (error && error.name === 'NotAllowedError') {
        return this.dataset.msgPermissionDenied;
      }

      if (error && error.name === 'NotFoundError') {
        return this.dataset.msgCancelled || this.dataset.msgNoDevice;
      }

      if (error && error.message === 'Invalid feature report payload') {
        return this.dataset.msgInvalidReport;
      }

      const prefix = this.dataset.msgErrorPrefix || 'Error';
      return `${prefix}: ${error && error.message ? error.message : 'Unknown error'}`;
    }

    async handleCheckClick() {
      if (!('hid' in navigator)) {
        this.hideResult();
        this.setStatus(this.dataset.msgUnsupported);
        return;
      }

      this.hideResult();
      this.setLoadingState(true);
      this.setStatus(this.dataset.msgChecking);

      let device;

      try {
        device = await this.requestDualSenseDevice();
        if (!device.opened) {
          await device.open();
        }

        const featureReport = await device.receiveFeatureReport(this.reportId);
        const model = this.parseBdmModel(featureReport);
        const modelPrefix = this.dataset.msgModelPrefix || 'Model';
        const successMessage = this.dataset.msgSuccess || 'Detection complete';

        this.setResult(model, this.getDeviceLabel(device));
        this.setStatus(`${successMessage} ${modelPrefix}: ${model}`);
      } catch (error) {
        this.hideResult();
        this.setStatus(this.mapErrorToMessage(error));
      } finally {
        this.setLoadingState(false);

        if (device && device.opened) {
          try {
            await device.close();
          } catch (closeError) {
            // No-op: closing is best-effort.
          }
        }
      }
    }
  }

  customElements.define('bdm-model-checker', BdmModelChecker);
}
